<template>
	<div class="promotion" :class="`promotion--${variant}`">
		<div class="promotion__text">
			<Text font="--text-18-22-med" color="--text-dark-primary">Get more with Kova app</Text>
			<Text font="--text-16-22-reg" color="--text-dark-secondary">
				Stay on track with shot reminders, track your symptoms, and get support along the way.
			</Text>
		</div>
		<QrcodeVue
			v-if="variant === 'qr'"
			class="promotion__qr"
			:value="APP_QR_URL"
			:size="86"
			level="L"
			render-as="svg"
			aria-label="Scan to download the Kova app"
		/>
		<div v-else class="promotion__stores">
			<a
				class="promotion__store"
				:href="APP_STORE_URL"
				target="_blank"
				rel="noopener noreferrer"
				aria-label="Download on the App Store"
			>
				<img class="promotion__badge promotion__badge--app-store" :src="appStoreBadge" alt="" />
			</a>
			<a
				class="promotion__store"
				:href="GOOGLE_PLAY_URL"
				target="_blank"
				rel="noopener noreferrer"
				aria-label="Get it on Google Play"
			>
				<img class="promotion__badge promotion__badge--google-play" :src="googlePlayBadge" alt="" />
			</a>
		</div>
	</div>
</template>

<script setup lang="ts">
import QrcodeVue from 'qrcode.vue'
import { Text } from '@simple-life-app/growth-ui-kova'
import appStoreBadge from '@/assets/portal/badge-app-store.svg'
import googlePlayBadge from '@/assets/portal/badge-google-play.svg'
import { APP_QR_URL, APP_STORE_URL, GOOGLE_PLAY_URL } from '@/portal/appPromotion'

/**
 * The contextual app promotion (PRD 3.1; KOVA-796 scope 5–7): a title, a subtitle and — `qr` — the QR
 * code at the bottom of the desktop sidebar (Figma 14492:84143), or — `stores` — the App Store and Google
 * Play buttons at the bottom of the mobile side menu (Figma 14254:175889). It sits out of the way of
 * the content and the main actions; nothing waits on it. KOVA-802 adds its tracking calls to the two
 * store links here (the approved events are not in the SDK yet).
 */

defineProps<{
	variant: 'qr' | 'stores'
}>()
</script>

<style scoped>
.promotion {
	display: flex;
	padding: var(--size-20);
	background: var(--background-white);
}

.promotion__text {
	display: flex;
	flex: 1 1 auto;
	flex-direction: column;
	gap: var(--size-8);
	min-width: 0;
}

/* Desktop: text beside the 86px QR (its own size), in a 36px-radius card. */
.promotion--qr {
	align-items: center;
	gap: var(--size-8);
	border-radius: var(--size-36);
}

.promotion__qr {
	display: block;
	flex: none;
}

/* Mobile: text over the two store buttons, in a 24px-radius card. */
.promotion--stores {
	flex-direction: column;
	gap: var(--size-20);
	border-radius: var(--size-24);
}

.promotion__stores {
	display: flex;
	gap: var(--size-8);
}

.promotion__store {
	display: flex;
	flex: 1 1 0;
	align-items: center;
	justify-content: center;
	min-width: 0;
	height: var(--size-48);
	border-radius: var(--size-20);
	background: var(--button-active-black);
}

.promotion__store:focus-visible {
	outline: 2px solid var(--stroke-accent-brand);
	outline-offset: 2px;
}

.promotion__badge {
	display: block;
	max-width: calc(100% - var(--size-16));
	width: auto;
}

/* The badge art at the size it has in the frame (106.72×30 and 94.54×22.5). */
.promotion__badge--app-store {
	height: 30px;
}

.promotion__badge--google-play {
	height: 22.5px;
}
</style>
